/**
 * **键盘输入** —— 纯逻辑那一半。挂在 DOM 上的那一半在 `render/keyboard.ts`。
 *
 * ## 为什么完全不用 DOM 输入框（这段是第 6 步实测推翻原方案之后的结论）
 *
 * 原始方案是留一个透明的 DOM `<input>` 承接输入法。**实测后推翻了。**
 *
 * 问题出在输入法本身：只要页面上有**任何可编辑元素获得焦点**，输入法就会接管按键。
 * 中文状态下 `keydown` 收到的 `key` 是 `'Process'`，或者干脆收不到。
 *
 * 而本项目 V1 的文本输入只有两处：**昵称**和**房间号**，都是短英文串。于是：
 *
 * > **昵称和房间号只接受英文 / 数字 / `_` / `-`，键盘在页面层面直接读 `keydown`，
 * > 不放任何 DOM 输入控件。**
 *
 * 这样输入法根本不会被激活，`Process` 那类问题从根上不存在。
 *
 * 代价（明确记下）：**中文昵称、剪贴板粘贴、光标选区都不支持。**
 * 将来真要支持中文昵称，再把这个例外加回来。
 *
 * ## 为什么把这一半单独拿出来
 *
 * `TextBuffer` 和 `KeyRouter` 都是**普通对象**，不 import Pixi 也不碰 `window`
 * —— 于是"退格到底退对了没有""叠加层会不会抢走底下的按键"这些能在 node 里断言。
 * 这类 bug 在浏览器里表现为"偶尔按不动"，靠手点是复现不出来的。
 */

/** 允许键入的字符：英文、数字、以及名字里用得上的两个符号 */
const ALLOWED_CHAR = /^[A-Za-z0-9_-]$/;

/** 这个按键能不能作为"文本"打进名字里 */
export function isTypableChar(key: string): boolean {
  return ALLOWED_CHAR.test(key);
}

/**
 * 一个按键事件。
 *
 * ⚠️ `key` 是**玩家实际打出来的那个字符** —— 该大写就大写，不在这里做任何
 * 大小写归一化。大小写是**字段**的事，由 `TextBuffer` 的 `accepts` /
 * `transform` 决定：
 *
 * - 房间号要**只有大写**（字母表里没有小写），所以它传 `transform: toUpperCase`
 * - 昵称要**保留玩家打的样子**，所以它什么都不传
 *
 * 第一版在这一层统一转小写，后果是**昵称永远打不出大写字母** ——
 * 而且因为"房间号用 transform 转大写"这条刚好也能工作，
 * 这个 bug 只在昵称上显形，很容易被当成"设计如此"。
 */
export interface KeyEvent {
  readonly key: string;
  readonly shift: boolean;
  /**
   * 原始事件。**只有需要 `preventDefault()` 的调用方才碰它**
   * （比如空格要拦住页面滚动）。
   */
  readonly raw: { preventDefault(): void } | null;
}

/** 谁能吃按键。返回 `true` = 吃掉了，不再往下传 */
export interface KeyHandler {
  onKey(event: KeyEvent): boolean;
}

export interface TextBufferOptions {
  /** 最长几个字符 */
  readonly maxLength: number;
  /** 逐字符过滤。默认是昵称那套（英文/数字/`_`/`-`） */
  readonly accepts?: (ch: string) => boolean;
  /** 接受之后的规范化。房间号用它转大写 */
  readonly transform?: (value: string) => string;
}

/**
 * 一个极简的"英文输入框"的**状态**（不含任何画法）。
 *
 * 没有光标、没有选区、没有剪贴板 —— 只支持打字和退格。
 * 够用就好：玩家名和房间号都是短英文串。
 */
export class TextBuffer {
  readonly #maxLength: number;
  readonly #accepts: (ch: string) => boolean;
  readonly #transform: (value: string) => string;
  #value = '';

  constructor(options: TextBufferOptions) {
    this.#maxLength = options.maxLength;
    this.#accepts = options.accepts ?? isTypableChar;
    this.#transform = options.transform ?? ((value) => value);
  }

  get value(): string {
    return this.#value;
  }

  set value(next: string) {
    this.#value = this.#clean(next);
  }

  get maxLength(): number {
    return this.#maxLength;
  }

  get isEmpty(): boolean {
    return this.#value === '';
  }

  /**
   * 处理一个按键。
   *
   * 返回**有没有变化**（不是"有没有吃掉"）—— 这样调用方能精确地只在需要时重绘。
   * 退格到空、或者打了第 25 个字符，都是"吃掉但没变化"。
   */
  handleKey(key: string): boolean {
    if (key === 'Backspace') {
      if (this.#value.length === 0) return false;
      this.#value = this.#value.slice(0, -1);
      return true;
    }
    if (key.length !== 1) return false;

    // ⚠️ **判断"收不收"要用规范化之后的那个字符，不是键盘给的原始字符。**
    //
    //    键盘层刻意把单字符统一转成小写（见 `KeyEvent` 的说明），而这里的
    //    `transform` 可能又把它变回大写 —— 房间号正是这样（字母表里只有大写字母）。
    //    拿原始的小写字符去比对一份只有大写的字母表，会**一个字符都收不进来**。
    //
    //    症状特别有迷惑性：输入框有焦点（光标在闪）、按键也确实被吃掉了
    //    （路由返回 true），但**一个字都打不进去**。真浏览器冒烟测试抓到的就是这个。
    const shaped = this.#transform(key);
    if (shaped.length !== 1 || !this.#accepts(shaped)) return false;

    const grown = this.#transform(this.#value + key);
    if (grown.length > this.#maxLength) return false;
    if (grown === this.#value) return false;
    this.#value = grown;
    return true;
  }

  /**
   * **粘贴一串文本。** 逐字符走和 `handleKey` 完全相同的过滤 / 规范化 / 上限，
   * 所以粘贴和手打的结果一模一样。返回有没有变化。
   */
  insert(text: string): boolean {
    const before = this.#value;
    for (const ch of text) {
      // 非法字符 / 已到上限都返回 false；继续试后面的字符（可能还有合法的）
      this.handleKey(ch);
    }
    return this.#value !== before;
  }

  clear(): boolean {
    if (this.#value === '') return false;
    this.#value = '';
    return true;
  }

  /** 洗一遍：丢掉非法字符、按上限截断、应用 transform */
  #clean(input: string): string {
    let out = '';
    for (const ch of input) {
      if (out.length >= this.#maxLength) break;
      // ⚠️ 和 `handleKey` 同一条规矩：判断"收不收"要用**规范化之后**的字符。
      //    不然 `buffer.value = 'abcd'` 这种赋值会被整段丢掉
      //    （字母表只有大写，而传进来的是小写）—— 而且它和"打字打不进去"
      //    是同一个根因的两个出口，改一处不改另一处就会时灵时不灵
      const shaped = this.#transform(ch);
      if (shaped.length !== 1 || !this.#accepts(shaped)) continue;
      out += ch;
    }
    return this.#transform(out);
  }
}

/**
 * **后进先出的按键路由。**
 *
 * 谁在上面谁先看。菜单栈里每打开一层就 push 一个处理者，
 * 关掉时 pop —— 于是"设置盖在游戏上时，按键该给谁"这件事
 * 和"谁画在上面"用的是**同一个顺序**，不可能对不上。
 *
 * ⚠️ 一个键如果上面那层返回 `false`，会继续往下传。这是**故意的**：
 * 设置页不该吃掉 WASD，底下的游戏仍然能收到。只有文本输入框
 * 才在拿到焦点时把可打印字符全吃掉。
 */
export class KeyRouter {
  readonly #handlers: KeyHandler[] = [];

  /** 压入一个处理者，返回"移除它"的函数 */
  push(handler: KeyHandler): () => void {
    this.#handlers.push(handler);
    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      const at = this.#handlers.lastIndexOf(handler);
      if (at >= 0) this.#handlers.splice(at, 1);
    };
  }

  get depth(): number {
    return this.#handlers.length;
  }

  /**
   * 派发一个按键。返回**有没有被吃掉**。
   *
   * 从最上面往下问，第一个返回 `true` 的赢。
   */
  dispatch(event: KeyEvent): boolean {
    for (let i = this.#handlers.length - 1; i >= 0; i--) {
      if (this.#handlers[i]?.onKey(event) === true) return true;
    }
    return false;
  }

  clear(): void {
    this.#handlers.length = 0;
  }
}
